import { Link2Icon, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DamageType } from '@/common/damage';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { DamageListDialog } from './DamageListDialog';

export type AddLineItemDialog = {
	onAddNewLineItem: (damages?: DamageType[]) => void;
	open: boolean;
	setOpen: (open: boolean) => void;
	alreadyAddedDamagesIds: Set<string>;
};
export function AddLineItemDialog({ onAddNewLineItem, open, setOpen, alreadyAddedDamagesIds }: AddLineItemDialog) {
	const { t } = useTranslation();
	const [damageListOpen, setDamageListOpen] = useState(false);
	const handleSubmit = () => {
		onAddNewLineItem();
	};
	const onDamageSelect = (damages: DamageType[]) => {
		onAddNewLineItem(damages);
		setDamageListOpen(false);
	};
	const openDamageDialog = () => {
		setDamageListOpen(true);
	};
	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t('finance.items.addLineItem')}</DialogTitle>
				</DialogHeader>
				<div className="grid grid-cols-2 gap-4 py-4">
					<Button
						role="button"
						variant="outline"
						className="group h-full w-full relative flex flex-col items-center justify-center p-6 bg-white border-2 border-zinc-300 rounded-lg hover:border-primary hover:bg-blue-50 transition-all cursor-pointer"
						onClick={handleSubmit}
					>
						<div className="w-16 h-16 bg-accent group-hover:bg-blue-100 dark:group-hover:bg-accent rounded-full flex items-center justify-center mb-3 transition-colors">
							<Plus className="size-8 group-hover:text-primary" />
						</div>
						<span className="text-sm font-medium group-hover:text-primary text-center">
							{t('finance.items.emptyItem')}
						</span>
					</Button>
					<Button
						variant="outline"
						className="group h-full w-full relative flex flex-col items-center justify-center p-6 bg-white border-2 border-zinc-300 rounded-lg hover:border-primary hover:bg-blue-50 transition-all cursor-pointer"
						onClick={openDamageDialog}
					>
						<div className="w-16 h-16 bg-accent group-hover:bg-blue-100 dark:bg-accent rounded-full flex items-center justify-center mb-3 transition-colors">
							<Link2Icon className="size-8 group-hover:text-primary" />
						</div>
						<span className="text-sm font-medium group-hover:text-primary text-center">
							{t('finance.items.fromDamages')}
						</span>
					</Button>
					<DamageListDialog
						open={damageListOpen}
						setOpen={setDamageListOpen}
						onDamageSelect={onDamageSelect}
						isMultiSelect
						alreadyAddedDamagesIds={alreadyAddedDamagesIds}
					/>
				</div>
				<DialogFooter>
					<DialogClose asChild>
						<Button variant="outline">{t('common.actions.cancel')}</Button>
					</DialogClose>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
